import * as React from "react";
import "./dropDown.scss";
import {Icon} from "../../atoms/Icon/Icon";
import DropDownList from "../DropDownList/DropDownList";
import {isNullOrUndefined} from "util";
import LabelWithIcon from "../../atoms/Label/LabelWithIcon";
import classNames = require("classnames");

interface IProps<T> {
	/**
	 * List of items.
	 */
	items: T[];
	/**
	 * Converts item to item label.
	 * @param {T} item
	 * @return {string}
	 */
	itemToLabel?: (item: T) => string | JSX.Element;
	/**
	 * Converts item to item id.
	 * @param {T} item
	 * @return {string}
	 */
	itemToId?: (item: T) => string;
	/**
	 * On delete callback.
	 * @param {string} id
	 */
	onSelect?: (item: T) => void;
	/**
	 * Selected item id.
	 */
	selectedItemId?: string;
	/**
	 * Input placeholder.
	 */
	placeholder?: string;

	autoWidth?: boolean;
	/**
	 * Indicates if input field has clear icon.
	 */
	clearable?: boolean;
	/**
	 * Clear icon click callback
	 */
	onClearClick?: () => void
	/**
	 * Display drop down on top
	 */
	dropDownOnTop?: boolean
	/**
	 * Indicates if field is disabled.
	 */
	disabled?: boolean;
	/**
	 * Indicates if item is disabled.
	 * @param {T} item
	 * @return {boolean}
	 */
	itemDisabled?: (item: T) => boolean;
}

interface IState {
	/**
	 * Indicates if dropdown is visible.
	 */
	dropDownVisible: boolean;
	/**
	 * Selected item id.
	 */
	selectedItemId: string | null | undefined;
}

export default class DropDown<T> extends React.Component<IProps<T>, IState> {

	constructor(props: IProps<T>) {
		super(props);
		this.state = {
			dropDownVisible: false,
			selectedItemId: props.selectedItemId
		};
		this.onDropDownToggle = this.onDropDownToggle.bind(this);
		this.onItemSelect = this.onItemSelect.bind(this);
		this.getSelectedItem = this.getSelectedItem.bind(this);
		this.selectedItemToValue = this.selectedItemToValue.bind(this);
		this.handleOutsideClick = this.handleOutsideClick.bind(this);
	}

	dropdown: HTMLDivElement;

	componentWillReceiveProps(newProps: IProps<T>) {
		this.setState({selectedItemId: newProps.selectedItemId});
	}

	componentDidMount() {
		document.addEventListener("mousedown", this.handleOutsideClick);
	}

	componentWillUnmount() {
		document.removeEventListener('mousedown', this.handleOutsideClick);
	}

	render() {
		const cn = classNames({
			"m-dropdown": true,
			"is-top": this.props.dropDownOnTop,
			"is-bottom": !this.props.dropDownOnTop,
		});
		return (
			<div ref={(dd) => this.dropdown = dd} className={"m-dropdown-menu-wrapper"}>
				<LabelWithIcon appendIcon={Icon.ArrowDown}
							   value={this.selectedItemToValue()}
							   placeholder={this.props.placeholder}
							   onClick={this.onDropDownToggle}
							   onAppendIconClick={this.onDropDownToggle}
							   clearable={this.props.clearable}
							   onClearClick={this.props.onClearClick}
							   disabled={this.props.disabled}/>
				{this.state.dropDownVisible &&
				<div className={cn} style={{width: this.props.autoWidth ? "auto" : ""}}>
					<DropDownList items={this.props.items}
								  itemToId={this.props.itemToId}
								  itemToLabel={this.props.itemToLabel}
								  selectedItemId={this.state.selectedItemId}
								  itemDisabled={this.props.itemDisabled}
								  onSelect={this.onItemSelect}/>
				</div>
				}
			</div>
		);
	}

	/**
	 * Map selected item (if any) to displayable value.
	 * @return {string}
	 */
	selectedItemToValue(): string | JSX.Element {
		const selectedItem = this.getSelectedItem(this.state.selectedItemId);
		return isNullOrUndefined(selectedItem) ? "" : this.props.itemToLabel(selectedItem);
	}

	/**
	 * Gets selected item from items list.
	 * @param {string} itemId
	 * @return {any}
	 */
	getSelectedItem(itemId: string) {
		return this.props.items.filter((item) => this.props.itemToId(item) === itemId)[0];
	}

	/**
	 * On item selected.
	 * @param {T} item
	 */
	onItemSelect(item: T) {
		this.setState({dropDownVisible: false, selectedItemId: this.props.itemToId(item)});
		if (!isNullOrUndefined(this.props.onSelect))
			this.props.onSelect(item);
	}

	onDropDownToggle() {
		if (!this.props.disabled)
			this.setState({dropDownVisible: !this.state.dropDownVisible});
	}

	/**
	 * Handle click outside dropdown list.
	 */
	handleOutsideClick(event: any) {
		if (!event || (this.dropdown && !this.dropdown.contains(event.target as HTMLElement))) {
			this.setState({dropDownVisible: false})
		}
	}
}
